import { useEffect, useRef, useCallback } from 'react'

/**
 * Hook personalizado para manejar el resize del mapa de Mapbox de manera eficiente
 * @param {Object} map - Instancia del mapa de Mapbox
 * @param {boolean} mapLoaded - Estado de carga del mapa
 * @param {Array} dependencies - Array de dependencias que triggerean el resize
 * @param {number} debounceMs - Tiempo de debounce en milisegundos (default: 150)
 */
export const useMapResize = (map, mapLoaded, dependencies = [], debounceMs = 150) => {
  const resizeObserverRef = useRef(null)
  const mapContainerRef = useRef(null)
  const timeoutRef = useRef(null)

  // 🔧 Función debounceada simple para resize
  const debouncedResize = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      if (map && map.resize && mapLoaded) {
        try {
          map.resize()
        } catch (error) {
          console.error('Error al redimensionar el mapa:', error)
        }
      }
    }, debounceMs)
  }, [map, mapLoaded, debounceMs])

  // 🔧 Función para trigger manual del resize
  const triggerResize = useCallback(() => {
    debouncedResize()
  }, [debouncedResize])

  const dependencySignature = JSON.stringify(dependencies)

  // 🔧 Setup del ResizeObserver
  useEffect(() => {
    if (!map || !mapLoaded) return

    // Buscar el contenedor del mapa si no se ha establecido
    if (!mapContainerRef.current) {
      const mapContainer = map.getContainer()
      mapContainerRef.current = mapContainer
    }

    // Crear ResizeObserver si no existe
    if (!resizeObserverRef.current && mapContainerRef.current) {
      resizeObserverRef.current = new ResizeObserver(entries => {
        for (const entry of entries) {
          // Verificar que realmente cambió el tamaño
          const { width, height } = entry.contentRect
          if (width > 0 && height > 0) {
            debouncedResize()
          }
        }
      })

      resizeObserverRef.current.observe(mapContainerRef.current)
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
        resizeObserverRef.current = null
      }
    }
  }, [map, mapLoaded, debouncedResize])

  // 🔧 Listener para cambios de orientación
  useEffect(() => {
    const handleOrientationChange = () => {
      // Esperar a que termine la animación de orientación
      setTimeout(() => {
        triggerResize()
      }, 500)
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Cuando la pestaña vuelve a ser visible
        setTimeout(() => {
          triggerResize()
        }, 100)
      }
    }

    window.addEventListener('orientationchange', handleOrientationChange)
    window.addEventListener('resize', triggerResize)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange)
      window.removeEventListener('resize', triggerResize)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [triggerResize])

  // 🔧 Trigger resize cuando cambien las dependencias
  useEffect(() => {
    if (dependencies.length > 0) {
      // Usar requestAnimationFrame para asegurar que el DOM se actualice primero
      const rafId = requestAnimationFrame(() => {
        triggerResize()
      })

      return () => {
        cancelAnimationFrame(rafId)
      }
    }
  }, [dependencies.length, dependencySignature, triggerResize])

  // 🔧 Cleanup en unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }
    }
  }, [])

  return { triggerResize }
}

/**
 * Hook alternativo más simple para casos específicos
 */
export const useSimpleMapResize = (map, mapLoaded, trigger) => {
  useEffect(() => {
    if (!map || !mapLoaded) return

    const timeoutId = setTimeout(() => {
      if (map.resize) {
        map.resize()
      }
    }, 200)

    return () => clearTimeout(timeoutId)
  }, [map, mapLoaded, trigger])
}
